import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Bike,
  Settings2,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeaturedFleet({ onBookNow }) {
  const { t } = useTranslation();
  const [motos, setMotos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);

  useEffect(() => {
    axios
      .get(`${API}/motos`)
      .then((r) => {
        const list = r.data.motos || [];
        setMotos(list);
        const f = list.findIndex((m) => m.featured);
        if (f >= 0) setIdx(f);
      })
      .catch(() => setMotos([]));
  }, []);

  if (!motos.length) return null;
  const m = motos[idx];
  const total = motos.length;

  const next = () => {
    setDir(1);
    setIdx((i) => (i + 1) % total);
  };
  const prev = () => {
    setDir(-1);
    setIdx((i) => (i - 1 + total) % total);
  };

  return (
    <section
      data-testid="featured-fleet"
      className="relative overflow-hidden bg-brand-black py-16 md:py-24"
    >
      {/* big diagonal orange backdrop */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-0 hidden h-full w-1/3 bg-brand-orange md:block"
        style={{ clipPath: "polygon(20% 0, 100% 0, 100% 100%, 0% 100%)" }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand-orange">
              {t("rentals.featured")}
            </span>
            <h2 className="mt-2 font-display text-5xl uppercase leading-[0.95] text-white sm:text-6xl">
              {t("rentals.title")}
            </h2>
          </div>
          <Link
            to="/rentals"
            data-testid="featured-view-all"
            className="group inline-flex items-center gap-2 border-2 border-white px-5 py-3 font-display text-base uppercase tracking-wider text-white hover:border-brand-orange hover:bg-brand-orange"
          >
            {t("rentals.viewAll")}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
          {/* MAIN CARD with arrow nav */}
          <div className="relative md:col-span-8">
            <AnimatePresence mode="wait" custom={dir}>
              <motion.article
                key={m.id}
                data-testid={`featured-card-${m.id}`}
                custom={dir}
                initial={{ opacity: 0, x: dir * 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -dir * 60 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="relative grid grid-cols-1 overflow-hidden border-2 border-brand-orange bg-brand-black hard-shadow-orange md:grid-cols-2"
              >
                <div className="relative aspect-square overflow-hidden bg-white">
                  <img src={m.image} alt={m.name} className="h-full w-full object-cover" />
                  <div className="absolute left-3 top-3 bg-brand-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                    {m.category}
                  </div>
                  <div className="absolute right-3 top-3 bg-brand-black/80 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur">
                    {idx + 1} / {total}
                  </div>
                </div>
                <div className="flex flex-col p-6 md:p-8">
                  <h3 className="font-display text-4xl uppercase leading-none text-white sm:text-5xl">
                    {m.name}
                  </h3>
                  <ul className="mt-4 flex flex-wrap gap-3 text-xs font-bold uppercase tracking-widest text-white/70">
                    <li className="inline-flex items-center gap-1.5">
                      <Bike className="h-3.5 w-3.5 text-brand-orange" />
                      {m.engine}
                    </li>
                    <li className="inline-flex items-center gap-1.5">
                      <Settings2 className="h-3.5 w-3.5 text-brand-orange" />
                      {m.transmission}
                    </li>
                  </ul>
                  <p className="mt-4 text-sm leading-relaxed text-white/80">{m.best_for}</p>

                  <div className="mt-6 grid grid-cols-3 gap-3 border-t border-white/10 pt-5">
                    <div>
                      <div className="font-display text-3xl text-brand-orange sm:text-4xl">
                        ${m.price_day_usd}
                      </div>
                      <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">
                        {t("rentals.perDay")}
                      </div>
                    </div>
                    <div>
                      <div className="font-display text-3xl text-white sm:text-4xl">
                        ${m.price_week_usd}
                      </div>
                      <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">
                        {t("rentals.perWeek")}
                      </div>
                    </div>
                    {m.price_month_usd && (
                      <div>
                        <div className="font-display text-3xl text-white sm:text-4xl">
                          ${m.price_month_usd}
                        </div>
                        <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">
                          {t("rentals.perMonth")}
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    data-testid={`featured-reserve-${m.id}`}
                    onClick={() => onBookNow?.(m.name)}
                    className="mt-6 h-12 rounded-none bg-brand-orange font-display text-lg uppercase tracking-wider text-white hover:bg-white hover:text-brand-orange"
                  >
                    {t("rentals.reserve")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </motion.article>
            </AnimatePresence>

            {/* PREV / NEXT arrow buttons */}
            <button
              data-testid="featured-prev-btn"
              onClick={prev}
              aria-label="Previous motorcycle"
              className="absolute -left-3 top-1/2 z-10 hidden h-14 w-14 -translate-y-1/2 items-center justify-center border-2 border-brand-orange bg-brand-black text-white shadow-lg transition hover:bg-brand-orange md:flex"
            >
              <ChevronLeft className="h-7 w-7" />
            </button>
            <button
              data-testid="featured-next-btn"
              onClick={next}
              aria-label="Next motorcycle"
              className="absolute -right-3 top-1/2 z-10 hidden h-14 w-14 -translate-y-1/2 items-center justify-center border-2 border-brand-orange bg-brand-black text-white shadow-lg transition hover:bg-brand-orange md:flex"
            >
              <ChevronRight className="h-7 w-7" />
            </button>

            {/* Mobile arrow row */}
            <div className="mt-4 flex items-center justify-between gap-3 md:hidden">
              <button
                data-testid="featured-prev-mobile"
                onClick={prev}
                className="inline-flex items-center gap-2 border-2 border-brand-orange bg-brand-black px-4 py-2 text-xs font-bold uppercase tracking-widest text-white hover:bg-brand-orange"
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <span className="text-xs font-bold uppercase tracking-widest text-white/70">
                {idx + 1} / {total}
              </span>
              <button
                data-testid="featured-next-mobile"
                onClick={next}
                className="inline-flex items-center gap-2 border-2 border-brand-orange bg-brand-black px-4 py-2 text-xs font-bold uppercase tracking-widest text-white hover:bg-brand-orange"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Dots indicator */}
            <div className="mt-4 hidden items-center justify-center gap-2 md:flex">
              {motos.map((mm, i) => (
                <button
                  key={mm.id}
                  data-testid={`featured-dot-${i}`}
                  onClick={() => {
                    setDir(i > idx ? 1 : -1);
                    setIdx(i);
                  }}
                  aria-label={`Show ${mm.name}`}
                  className={`h-2 transition-all ${
                    i === idx ? "w-8 bg-brand-orange" : "w-2 bg-white/30 hover:bg-white/60"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* SIDEBAR */}
          <aside className="md:col-span-4 flex flex-col gap-4">
            <div className="border-2 border-white/15 bg-white/[0.03] p-6">
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand-orange">
                Why this bike
              </span>
              <h4 className="mt-2 font-display text-2xl uppercase text-white">{m.name}</h4>
              <ul className="mt-4 space-y-3 text-sm text-white/80">
                <li className="flex gap-2">
                  <span className="text-brand-orange">›</span> {m.transmission} — easy to ride
                </li>
                <li className="flex gap-2">
                  <span className="text-brand-orange">›</span> {m.engine} engine, fuel efficient
                </li>
                <li className="flex gap-2">
                  <span className="text-brand-orange">›</span> {m.best_for}
                </li>
                <li className="flex gap-2">
                  <span className="text-brand-orange">›</span> Helmet, lock and route tips included
                </li>
              </ul>
            </div>
            <div className="border-2 border-brand-orange bg-brand-orange p-6 text-white">
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/80">
                Best deal
              </span>
              <h4 className="mt-2 font-display text-3xl uppercase leading-none">
                Monthly ${m.price_month_usd || m.price_week_usd}
              </h4>
              <p className="mt-2 text-sm text-white/90">
                Stay a while? Save big with our weekly &amp; monthly rates vs. daily.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
